'use client';

import React from 'react';
import { CalendarStar } from '@phosphor-icons/react';
import { COLOR_THEME_YELLOW_PRIMARY_BASE } from '@/lib/variables';
import { LanguageType, LanguageDirection } from '@/lib/types';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(ScrollTrigger);

interface LegalContentSectionProps {
  language: 'english' | 'arabic';
  policyType?: 'cookies' | 'privacy';
}


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface LegalContentSectionProps {
  language: 'english' | 'arabic';
}

const LegalContentSection: React.FC<LegalContentSectionProps> = ({ language = "english" }) => {
  const isLg = useIsLg();

  const lastUpdatedDate = '26.11.2025';

  // Cookies Policy Content
  const cookiesPolicy = {
    title: {
      en: "RIVIVE Center Cookies Policy",
      ar: "سياسة ملفات تعريف الارتباط (Cookies) لمركزRIVIVE"
    },
    sections: [
      {
        number: 1,
        title: {
          en: "Introduction: What Are Cookies?",
          ar: "مقدمة: ما هي ملفات تعريف الارتباط؟"
        },
        content: {
          en: "Cookies are small text files that are placed on your device (computer, mobile phone, or tablet) by websites you visit. These files are widely used to make websites work more efficiently, as well as to provide information to the website owners. This policy aims to provide you with clear and comprehensive information about the cookies we use and the purposes for which we use them.",
          ar: "ملفات تعريف الارتباط (Cookies) هي ملفات نصية صغيرة يتم وضعها على جهازك (الكمبيوتر، الهاتف المحمول، أو الجهاز اللوحي) بواسطة المواقع الإلكترونية التي تزورها. تُستخدم هذه الملفات على نطاق واسع لجعل المواقع الإلكترونية تعمل بشكل أفضل وأكثر كفاءة، بالإضافة إلى توفير معلومات لأصحاب الموقع. تهدف هذه السياسة إلى تزويدك بمعلومات واضحة وشاملة حول الملفات التي نستخدمها والأغراض التي نستخدمها من أجلها."
        }
      },
      {
        number: 2,
        title: {
          en: "How We Use Cookies",
          ar: "كيف نستخدم ملفات تعريف الارتباط؟"
        },
        content: {
          en: "At RIVIVE Center, we use cookies for several key purposes, including:\n\n• Enhancing your experience: To ensure our website functions properly and smoothly.\n\n• Remembering your preferences: So you don't have to re-enter information every time you visit the site.\n\n• Performance analysis: To understand how visitors interact with our site, which helps us improve its content and functionality.\n\n• Security: To help protect our website and users from security threats.",
          ar: "نحن في مركز RIVIVE نستخدم ملفات تعريف الارتباط لعدة أغراض أساسية، منها:\n\n• تحسين تجربتك: لضمان عمل موقعنا الإلكتروني بشكل صحيح وسلس.\n\n• تذكر تفضيلاتك: لتجنب حاجتك إلى إعادة إدخال المعلومات في كل مرة تزور فيها الموقع.\n\n• تحليل الأداء: لفهم كيفية تفاعل الزوار مع موقعنا، مما يساعدنا على تحسين محتواه ووظائفه.\n\n• الأمان: للمساعدة في حماية موقعنا ومستخدميه من التهديدات الأمنية."
        }
      },
      {
        number: 3,
        title: {
          en: "Types of Cookies We Use",
          ar: "أنواع ملفات تعريف الارتباط التي نستخدمها"
        },
        content: {
          en: "We use the following types of cookies on our website:\n\n• A) Strictly Necessary Cookies:\n\nThese cookies are essential for operating our website and enabling you to navigate and use its basic features, such as accessing secure areas. Without these cookies, the services you've requested—such as appointment bookings—cannot be provided. These cookies do not require your consent.\n\n• B) Performance & Analytics Cookies:\n\nThese cookies collect information about how you use our website, for example, which pages you visit most often and if you receive any error messages. All information collected is anonymous and used solely to improve website functionality. We may use third-party services such as Google Analytics for this purpose.\n\n• C) Functionality Cookies:\n\nThese cookies allow the website to remember choices you make (such as your username, language, or region) and provide enhanced, more personalized features. For example, they can be used to remember your login details so you don't have to enter them every time.\n\n• D) Targeting & Advertising Cookies:\n\nWe or our third-party partners may use these cookies to deliver advertising content that we believe is relevant to you and your interests. They can also be used to limit how often you see an ad and to measure the effectiveness of advertising campaigns.",
          ar: "نحن نستخدم الأنواع التالية من ملفات تعريف الارتباط على موقعنا:\n\n• أ) ملفات تعريف الارتباط الضرورية جداً (Strictly Necessary Cookies):\n\no هذه الملفات لا غنى عنها لتشغيل موقعنا الإلكتروني وتمكينك من التنقل فيه واستخدام ميزاته الأساسية، مثل الوصول إلى المناطق الآمنة. بدون هذه الملفات، لا يمكن تقديم الخدمات التي طلبتها، مثل حجز المواعيد. لا تتطلب هذه الملفات موافقتك.\n\n• ب) ملفات تعريف الارتباط الخاصة بالأداء والتحليلات(Performance & Analytics Cookies):\n\no تقوم هذه الملفات بجمع معلومات حول كيفية استخدامك لموقعنا، على سبيل المثال، الصفحات التي تزورها غالبًا وأي رسائل خطأ قد تتلقاها. جميع المعلومات التي تجمعها هذه الملفات مجهولة الهوية وتُستخدم فقط لتحسين طريقة عمل الموقع. قد نستخدم خدمات من أطراف ثالثة مثلGoogle Analytics لهذا الغرض.\n\n• ج) ملفات تعريف الارتباط الوظيفية (Functionality Cookies):\n\no تسمح هذه الملفات للموقع بتذكر الخيارات التي تقوم بها (مثل اسم المستخدم، اللغة، أو المنطقة التي تتواجد فيها) وتوفير ميزات محسّنة وأكثر شخصية. على سبيل المثال، يمكن استخدامها لتذكر تفاصيل تسجيل الدخول الخاصة بك حتى لا تضطر إلى إعادة إدخالها في كل مرة.\n\n• د) ملفات تعريف الارتباط الخاصة بالاستهداف والإعلان(Targeting & Advertising Cookies):\n\no قد نستخدم نحن أو شركاؤنا من الأطراف الثالثة هذا النوع من الملفات لتقديم محتوى إعلاني نعتقد أنه قد يكون ذا صلة بك وباهتماماتك. يمكن استخدامها للحد من عدد المرات التي ترى فيها إعلانًا معينًا ولقياس فعالية الحملات الإعلانية."
        }
      },
      {
        number: 4,
        title: {
          en: "Third-Party Cookies",
          ar: "ملفات تعريف الارتباط من الأطراف الثالثة"
        },
        content: {
          en: "Our website may contain services or content from third parties (such as Google Maps or social media sharing buttons). These third parties may place their own cookies on your device when you interact with their content. We do not control the settings of these cookies and recommend reviewing the privacy policies of those third parties for more information.",
          ar: "قد يحتوي موقعنا على خدمات أو محتوى من أطراف ثالثة (مثل خرائط جوجل، أو أزرار المشاركة على وسائل التواصل الاجتماعي). قد تقوم هذه الأطراف الثالثة بوضع ملفات تعريف الارتباط الخاصة بها على جهازك عند تفاعلك مع هذا المحتوى. نحن لا نتحكم في إعدادات هذه الملفات، ونوصي بمراجعة سياسات الخصوصية الخاصة بهذه الأطراف للحصول على مزيد من المعلومات."
        }
      },
      {
        number: 5,
        title: {
          en: "Managing Cookie Settings",
          ar: "كيفية التحكم في إعدادات ملفات تعريف الارتباط"
        },
        content: {
          en: "You have the ability to accept or reject cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline them if you prefer. You can manage cookie settings through the \"Settings\" or \"Preferences\" menu in your browser.\n\nPlease note: If you choose to disable cookies, you may not be able to experience all the interactive features of our website fully, and this may negatively impact site functionality.",
          ar: "لديك القدرة على قبول أو رفض ملفات تعريف الارتباط. معظم متصفحات الويب تقبل ملفات تعريف الارتباط تلقائيًا، ولكن يمكنك عادةً تعديل إعدادات متصفحك لرفضها إذا كنت تفضل ذلك. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال قائمة \"الإعدادات\" أو \"التفضيلات\" في المتصفح الذي تستخدمه. يرجى ملاحظة: إذا اخترت تعطيل ملفات تعريف الارتباط، فقد لا تتمكن من تجربة جميع الميزات التفاعلية لموقعنا بشكل كامل وقد يؤثر ذلك سلباً على وظائف الموقع."
        }
      },
      {
        number: 6,
        title: {
          en: "Updates to This Policy",
          ar: "تحديثات هذه السياسة"
        },
        content: {
          en: "We may update this Cookie Policy from time to time to reflect changes in the technologies we use or to comply with new regulatory requirements. Any updates will be posted on this page.",
          ar: "قد نقوم بتحديث سياسة ملفات تعريف الارتباط هذه من وقت لآخر لتعكس أي تغييرات في التقنيات التي نستخدمها أو لتتوافق مع المتطلبات التنظيمية الجديدة. سيتم نشر أي تحديثات على هذه الصفحة."
        }
      },
      {
        number: 7,
        title: {
          en: "Contact Us",
          ar: "للتواصل معنا"
        },
        content: {
          en: "If you have any questions about our use of cookies, you can contact us via the information provided on our \"Privacy Policy\" page or the \"Contact Us\" page.",
          ar: "إذا كان لديك أي أسئلة حول استخدامنا لملفات تعريف الارتباط، يمكنك التواصل معنا عبر المعلومات المتاحة في صفحة \"سياسة الخصوصية\" أو صفحة \"اتصل بنا\"."
        }
      }
    ]
  };

  // Privacy Policy Content
  const privacyPolicy = {
    title: {
      en: "RIVIVE Center Privacy Policy",
      ar: "سياسة الخصوصية لمركز RIVIVE"
    },
    sections: [
      {
        number: 1,
        title: {
          en: "Introduction",
          ar: "مقدمة"
        },
        content: {
          en: "RIVIVE Center (\"we\" or \"the Center\") is committed to protecting your personal data privacy. This policy (\"Policy\") outlines our practices regarding the collection, processing, use, and protection of your personal information. It applies to all data we collect about you through our website, when you contact us, or during the provision of our services at the Center.",
          ar: "يلتزم مركز RIVIVE (\"نحن\" أو \"المركز\") بحماية خصوصية بياناتك الشخصية. توضح هذه السياسة (\"السياسة\") ممارساتنا المتعلقة بجمع معلوماتك الشخصية ومعالجتها واستخدامها وحمايتها. تنطبق هذه السياسة على جميع المعلومات التي نجمعها عنك من خلال موقعنا الإلكتروني، أو عند تواصلك معنا، أو خلال تقديم خدماتنا لك في المركز."
        }
      },
      {
        number: 2,
        title: {
          en: "Policy Updates",
          ar: "تحديث السياسة"
        },
        content: {
          en: "We reserve the right to modify or update this Policy at any time. Any changes will be posted on this page, and the \"Last Updated\" date at the top will be revised. Your continued use of the website or our services following such changes constitutes your acceptance of the updated Policy. We encourage you to review this page periodically.",
          ar: "نحتفظ بالحق في تعديل أو تحديث هذه السياسة في أي وقت. سيتم نشر أي تغييرات على هذه الصفحة، وسيتم تحديث تاريخ \"آخر تحديث\" في الأعلى. إن استمرارك في استخدام الموقع أو خدماتنا بعد نشر التغييرات يُعد موافقة منك على السياسة المحدثة. نشجعك على مراجعة هذه الصفحة بشكل دوري."
        }
      },
      {
        number: 3,
        title: {
          en: "Compliance",
          ar: "الامتثال"
        },
        content: {
          en: "This Policy has been prepared in accordance with the applicable laws and regulations in the Kingdom of Saudi Arabia, particularly the Personal Data Protection Law and its implementing regulations. We are committed to processing your personal data transparently, fairly, and in compliance with legal requirements.",
          ar: "تم إعداد هذه السياسة لتتوافق مع الأنظمة والقوانين المعمول بها في المملكة العربية السعودية، وعلى رأسها نظام حماية البيانات الشخصية ولائحته التنفيذية. نحن ملتزمون بمعالجة بياناتك الشخصية بشفافية وعدالة وبما يتماشى مع المتطلبات النظامية."
        }
      },
      {
        number: 4,
        title: {
          en: "Data Collection and Use",
          ar: "جمع البيانات واستخدامها"
        },
        content: {
          en: "We collect various types of data to help us deliver and improve our services:\n\n• Identity and Contact Information: Such as your name, phone number, email address, and date of birth.\n\n• Health and Lifestyle Information: Shared during consultations, including your general health status, aesthetic goals, dietary habits, and physical activity — essential for tailoring your treatment plan.\n\n• Technical Data: Automatically collected data such as IP address, browser type, device information, and your visit history to our website.\n\n• Transaction Data: Records of appointments, services received, and payment details.",
          ar: "نقوم بجمع أنواع مختلفة من البيانات لمساعدتنا في تقديم وتطوير خدماتنا:\n\n• بيانات الهوية والتواصل: مثل اسمك، ورقم هاتفك، وبريدك الإلكتروني، وتاريخ ميلادك.\n\n• البيانات الصحية وبيانات نمط الحياة: معلومات تشاركها معنا أثناء الاستشارات تتعلق بحالتك الصحية العامة، وأهدافك الجمالية، وطبيعة نظامك الغذائي ونشاطك البدني، وهي ضرورية لتصميم برنامجك العلاجي.\n\n• البيانات الفنية: معلومات تُجمع تلقائياً مثل عنوان بروتوكول الإنترنت (IP)، ونوع المتصفح، ومعلومات عن جهازك، وسجل زياراتك لصفحات الموقع.\n\n• بيانات المعاملات: سجلات المواعيد، والخدمات التي حصلت عليها، وتفاصيل الدفع."
        }
      },
      {
        number: 5,
        title: {
          en: "How We Use Your Data",
          ar: "كيف نستخدم بياناتك"
        },
        content: {
          en: "We use the personal data we collect for the following specific purposes:\n\n• To manage and schedule your appointments and deliver the requested services.\n\n• To personalize your aesthetic and treatment plans according to your needs and goals.\n\n• To communicate with you regarding inquiries or appointment reminders.\n\n• To process payments for our services.\n\n• To improve and enhance our service quality and website.\n\n• To send marketing materials and newsletters you may be interested in (with your prior consent).\n\n• To comply with our legal and regulatory obligations.",
          ar: "نستخدم بياناتك الشخصية التي نجمعها للأغراض المحددة التالية:\n\n• إدارة وحجز مواعيدك وتقديم الخدمات التي طلبتها.\n\n• تخصيص خطتك العلاجية والجمالية بناءً على احتياجاتك وأهدافك.\n\n• التواصل معك للرد على استفساراتك أو إرسال تذكيرات بالمواعيد.\n\n• معالجة المدفوعات المتعلقة بخدماتنا.\n\n• تحسين وتطوير جودة خدماتنا وموقعنا الإلكتروني.\n\n• إرسال الإعلانات والنشرات التسويقية التي قد تهمك (بموافقتك المسبقة).\n\n• الامتثال لالتزاماتنا القانونية والتنظيمية."
        }
      },
      {
        number: 6,
        title: {
          en: "Legal Basis for Data Processing",
          ar: "على أي أساس نعالج بياناتك"
        },
        content: {
          en: "We process your personal data based on the following legal grounds:\n\n• Consent: When you give us explicit consent for specific purposes, such as receiving marketing offers.\n\n• Contractual Necessity: When processing is required to fulfill our contract with you (e.g., providing booked services).\n\n• Legal Obligation: When required to comply with applicable laws or regulations.\n\n• Legitimate Interests: For legitimate business purposes, such as securing our network and services, provided this does not override your fundamental rights and freedoms.",
          ar: "نقوم بمعالجة بياناتك الشخصية بناءً على الأسس النظامية التالية:\n\n• الموافقة: عندما تمنحنا موافقتك الصريحة على معالجة بياناتك لغرض معين، مثل إرسال العروض التسويقية.\n\n• الضرورة التعاقدية: عندما تكون المعالجة ضرورية لتنفيذ عقدنا معك (أي لتقديم الخدمة التي حجزتها).\n\n• الالتزام النظامي: عندما يتوجب علينا معالجة بياناتك للامتثال لقانون أو لائحة تنظيمية.\n\n• المصالح المشروعة: لمعالجة البيانات من أجل تحقيق مصالحنا المشروعة، مثل تأمين وحماية شبكتنا وخدماتنا، شريطة ألا تتعارض مع حقوقك وحرياتك الأساسية."
        }
      },
      {
        number: 7,
        title: {
          en: "Marketing",
          ar: "التسويق"
        },
        content: {
          en: "Your awareness of this policy implies implicit consent to receive emails, SMS messages, or other disclosed communication methods — within the limits of relevant laws. If you wish to opt out, please contact us via the email address provided at the end of this policy.",
          ar: "يعتبر اطلاعكم على هذه السياسة موافقه ضمنيه وذلك من اجل ارسال بريد الكتروني او رسائل نصيه او أي من الطرق التي تم الإفصاح بها من قبلكم وبحدود الأنظمة ذات العلاقة وفي حال رفضكم الرجاء ارسال للبريد المذكور في اخر السياسة من اجل الغاء الاشتراك."
        }
      },
      {
        number: 8,
        title: {
          en: "Data Sharing",
          ar: "مع من نشارك بياناتك؟"
        },
        content: {
          en: "We do not sell your personal data. We share it only within a limited scope and with specific parties to fulfill the purposes stated in this Policy, including:\n\n• Authorized Staff: To discuss your case and create an appropriate treatment plan.\n\n• Third-Party Service Providers: Such as website hosting and appointment systems, who act on our behalf and are contractually bound to protect your data.\n\n• Governmental and Regulatory Authorities: If required by official request under applicable laws.",
          ar: "نحن لا نبيع بياناتك الشخصية. تتم مشاركة بياناتك فقط في نطاق محدود ومع أطراف محددة لتحقيق الأغراض المذكورة في هذه السياسة، وتشمل:\n\n• موظفونا المختصون والمرخصون: لمناقشة حالتك ووضع الخطة العلاجية المناسبة.\n\n• مزودو الخدمات من الأطراف الثالثة: مثل شركات استضافة المواقع وأنظمة إدارة المواعيد، والذين يعملون بالنيابة عنا ويلتزمون تعاقدياً بحماية بياناتك.\n\n• الجهات الحكومية والسلطات التنظيمية: في حال وجود طلب رسمي بموجب الأنظمة المعمول بها."
        }
      },
      {
        number: 9,
        title: {
          en: "Where Your Data Is Processed",
          ar: "أين سنعالج بياناتك"
        },
        content: {
          en: "Your data is primarily stored and processed on servers located within Saudi Arabia. If we engage third-party services requiring international data transfers, we ensure these are conducted in compliance with the Personal Data Protection Law and that an adequate level of protection is maintained.",
          ar: "يتم تخزين ومعالجة بياناتك الشخصية بشكل أساسي داخل خوادم تقع في المملكة العربية السعودية. في حال قمنا بالاستعانة بخدمات أطراف ثالثة تتطلب نقل البيانات خارج المملكة، سنتأكد من أن هذا النقل يتم وفقاً للضوابط والمتطلبات التي يحددها نظام حماية البيانات الشخصية لضمان توفير مستوى حماية كافٍ لبياناتك."
        }
      },
      {
        number: 10,
        title: {
          en: "Your Rights",
          ar: "حقوقك"
        },
        content: {
          en: "You have the following rights concerning your personal data:\n\n• Right to be Informed: Know the legal basis for collecting your data and the intended purpose.\n\n• Right of Access: Request a copy of the personal data we process about you.\n\n• Right to Rectification: Request to update or correct inaccurate or incomplete data.\n\n• Right to Withdraw Consent: You can withdraw your consent to data processing at any time (prior to receiving treatment).",
          ar: "لديك الحقوق التالية فيما يتعلق ببياناتك الشخصية:\n\n• الحق في العلم: معرفة الأساس النظامي لجمع بياناتك والغرض من ذلك.\n\n• الحق في الوصول: الحصول على نسخة من بياناتك الشخصية التي نعالجها.\n\n• الحق في التصحيح: طلب تحديث أو تصحيح بياناتك غير الدقيقة أو غير المكتملة.\n\n• الحق في سحب الموافقة: يمكنك سحب موافقتك على معالجة بياناتك في أي وقت (قبل إجراء العلاج)."
        }
      },
      {
        number: 11,
        title: {
          en: "Non-Personal Aggregate Data",
          ar: "المعلومات المجمعة الغير شخصية"
        },
        content: {
          en: "We may use non-personal, aggregated, and anonymized data (such as website visitor statistics) for analytical purposes and to enhance user experience. This data cannot be used to identify you.",
          ar: "قد نستخدم البيانات غير الشخصية (مثل إحصائيات زوار الموقع) بشكل مجمع ومجهول الهوية لأغراض التحليل الإحصائي وتحسين تجربة المستخدم. هذه البيانات لا يمكن استخدامها لتحديد هويتك."
        }
      },
      {
        number: 12,
        title: {
          en: "Providing Safe and Suitable Care",
          ar: "تقديم خدمات العناية والجمال بالشكل المناسب"
        },
        content: {
          en: "We use the information you provide to ensure the services and treatments offered are safe and appropriate for your condition. Providing accurate and complete information helps us avoid contraindications and deliver the highest level of care.",
          ar: "نستخدم المعلومات التي تقدمها لنا لضمان أن الخدمات والعلاجات المقدمة لك آمنة ومناسبة لحالتك. إن تقديم معلومات دقيقة وكاملة يساعدنا على تجنب أي موانع للعلاج وتقديم أفضل مستوى من الرعاية."
        }
      },
      {
        number: 13,
        title: {
          en: "Children",
          ar: "الأطفال"
        },
        content: {
          en: "Our services and website are intended for adults (18 years and older). We do not knowingly collect personal data from children without explicit parental consent.",
          ar: "خدماتنا وموقعنا الإلكتروني موجهة للأفراد البالغين (18 عاماً فما فوق). نحن لا نقوم بجمع معلومات شخصية عن قصد من الأطفال دون الحصول على موافقة صريحة من ولي الأمر."
        }
      },
      {
        number: 14,
        title: {
          en: "Data Security",
          ar: "أمن المعلومات"
        },
        content: {
          en: "We apply advanced technical and organizational security measures to protect your data from loss, unauthorized access, disclosure, or alteration. Our staff are trained on the importance of privacy and secure data handling.",
          ar: "نحن نطبق معايير أمنية تقنية وتنظيمية متقدمة لحماية بياناتك من الفقدان أو الوصول غير المصرح به أو الكشف أو التعديل. يتم تدريب موظفينا على أهمية حماية الخصوصية والتعامل الآمن مع البيانات."
        }
      },
      {
        number: 15,
        title: {
          en: "Data Retention",
          ar: "الاحتفاظ بالبيانات"
        },
        content: {
          en: "We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected or as required by applicable laws and regulations in Saudi Arabia, such as those related to health record retention.",
          ar: "نحتفظ ببياناتك الشخصية للمدة اللازمة لتحقيق الأغراض التي تم جمعها من أجلها، أو للمدة التي تتطلبها الأنظمة واللوائح السارية في المملكة، مثل الأنظمة المتعلقة بالاحتفاظ بالسجلات الصحية."
        }
      },
      {
        number: 16,
        title: {
          en: "Third-Party Websites",
          ar: "مواقع الجهات الخارجية"
        },
        content: {
          en: "Our website may contain links to other websites. We are not responsible for the content or privacy practices of those sites. We recommend reviewing their privacy policies before providing any information.",
          ar: "قد يحتوي موقعنا على روابط لمواقع إلكترونية أخرى. نحن غير مسؤولين عن محتوى أو سياسات الخصوصية لهذه المواقع. نوصيك بقراءة سياسات الخصوصية الخاصة بهم قبل تقديم أي معلومات."
        }
      },
      {
        number: 17,
        title: {
          en: "Inquiries and Complaints",
          ar: "الاستفسارات والشكاوى"
        },
        content: {
          en: "If you have any questions or complaints about this Policy or how we handle your data, you may contact our Data Protection Officer via:\n\nEmail: business@rivive.sa\n\nPhone: +966 56 249 2964",
          ar: "إذا كان لديك أي سؤال أو شكوى بخصوص هذه السياسة أو طريقة معالجتنا لبياناتك، يمكنك التواصل مع مسؤول حماية البيانات لدينا عبر:\n\n• البريد الإلكتروني: business@rivive.sa\n\n• الهاتف: +966 56 249 2964"
        }
      },
      {
        number: 18,
        title: {
          en: "Data Transfer in Case of Merger or Sale",
          ar: "نقل البيانات عند الاندماج أو البيع أو أي حدث آخر"
        },
        content: {
          en: "If RIVIVE Center undergoes a merger, acquisition, or sale of assets, client data may be transferred as part of the transaction. In such cases, we will notify you and ensure that the new entity upholds the same privacy standards outlined in this Policy.",
          ar: "في حال دخول مركز RIVIVE في عملية اندماج أو استحواذ أو بيع لأصوله، قد يتم نقل بيانات عملائنا كجزء من هذه الصفقة. في مثل هذه الحالة، سنحرص على إخطاركم وسنضمن أن الطرف الجديد يلتزم بنفس معايير حماية الخصوصية الموضحة في هذه السياسة."
        }
      }
    ]
  };

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let currentParagraph: string[] = [];
    let currentList: string[] = [];
    let inList = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={`para-${elements.length}`} className="text-18--alexandria text-text-tertiary pb-[0.75rem]">
            {currentParagraph.join('\n')}
          </p>
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="text-18--alexandria text-text-tertiary pb-[0.75rem] list-disc list-inside space-y-[0.5rem]">
            {currentList.map((item, idx) => (
              <li key={idx} className="pl-[0.5rem]">
                {item.replace(/^[•o]\s*/, '')}
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('o ')) {
        flushParagraph();
        inList = true;
        currentList.push(trimmedLine);
      } else if (trimmedLine === '') {
        if (inList) {
          flushList();
        } else {
          flushParagraph();
        }
      } else {
        if (inList) {
          flushList();
        }
        currentParagraph.push(trimmedLine);
      }
    });

    flushParagraph();
    flushList();

    return <div className="flex flex-col">{elements}</div>;
  };

  const renderPolicy = (policy: typeof cookiesPolicy) => {
    const title = policy.title[language === 'arabic' ? 'ar' : 'en'];

    return (
      <div className='flex flex-col w-full tb:max-w-[49.6875rem]'>
        <p className="text-36-to-24--neue-june text-text-primary w-full tb:max-w-[49.6875rem] pb-[2.5rem] lg:pb-[4.5rem]">
          {title}
        </p>
        {policy.sections.map((section, index) => (
          <div key={index} className={index > 0 ? 'pt-[2rem] lg:pt-[2.5rem]' : ''}>
            <div className='w-full tb:max-w-[49.6875rem] flex flex-col lg:flex-row gap-[1rem]'>
              <div className='flex flex-row w-full tb:max-w-[5.3125rem]'>
                <div className='
                  w-[44px]
                  h-[44px]
                  lg:w-[56px]
                  lg:h-[56px]
                  flex items-center justify-center
                  rounded-full bg-bg-light-primary border-main border-stroke-soft-200
                  p-[0.5rem] lg:p-[0.625rem]
                '>
                  <p className="text-24--neue-june text-text-disabled text-center w-fit">
                    {String(section.number).padStart(2, '0')}
                  </p>
                </div>
              </div>
              <div className='w-full flex flex-col gap-[1.5rem] lg:gap-[1.75rem]'>
                <p className="text-24--neue-june text-text-primary py-[0.625rem]">
                  {section.title[language === 'arabic' ? 'ar' : 'en']}
                </p>
                <div className="pb-[1.5rem] lg:pb-[2rem]">
                  {renderContent(section.content[language === 'arabic' ? 'ar' : 'en'])}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    // section has mixed padding!
    <section
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="w-full flex flex-col items-center justify-center
       gap-y-[3.5rem]
       padding-narrow-y-mobile
       padding-narrow-y-desktop
       padding-main-x-mobile
       padding-narrow-x-desktop
       ">

      <div className="w-full pt-[9rem] lg:pt-[13.5rem] pb-[2rem] px-[0.5rem] lg:px-[2rem]">
        <h1 className='text-120--neue-june text-text-primary'>
          Terms & Conditions
        </h1>
      </div>

      <div className="w-full container-large  flex flex-row gap-[5rem] px-[0.5rem] lg:px-[2rem]">
        <img src="/Support-Lockup-BadgeClean-Registration-None.svg" alt="Support lockup clean badge"
          style={{
            height: 'clamp(4.5rem, 2.2421rem + 4.71vw, 7.75rem)'
          }}
          className='hidden lg:block'
        />

        <div className=" w-full container-small">
          <div className="w-full flex flex-col gap-y-[2.5rem] lg:gap-y-[4.5rem]">
            {renderPolicy(cookiesPolicy)}

            <div className='w-full tb:max-w-[49.6875rem] flex flex-row gap-[0.5rem] lg:gap-[0.75rem] pt-[2rem] lg:pt-[3.5rem]'>
              <CalendarStar size={isLg ? 28 : 20} color={COLOR_THEME_YELLOW_PRIMARY_BASE} />
              <p className="text-20--alexandria text-text-tertiary">
                {language === 'arabic' ? `آخر تحديث: ${lastUpdatedDate}` : `Last updated: ${lastUpdatedDate}`}
              </p>
            </div>

            {renderPolicy(privacyPolicy)}

            <div className='w-full tb:max-w-[49.6875rem] flex flex-row gap-[0.5rem] lg:gap-[0.75rem] pt-[2rem] lg:pt-[3.5rem]'>
              <CalendarStar size={isLg ? 28 : 20} color={COLOR_THEME_YELLOW_PRIMARY_BASE} />
              <p className="text-20--alexandria text-text-tertiary">
                {language === 'arabic' ? `آخر تحديث: ${lastUpdatedDate}` : `Last updated: ${lastUpdatedDate}`}
              </p>
            </div>
          </div>
        </div>
      </div>

    </section >
  );
};

export default LegalContentSection;
